import { SETTINGS, COLORS } from '../config/settings';

export class ChainSystem {
  private citizens: number = 0;
  private goal: number = 0;
  private currentRegion: number = 1;
  private doubleChainMode: boolean = false;
  private doubleChainTimer: number = 0;

  constructor() {
    this.calculateGoal();
  }

  private calculateGoal(): void {
    // Goal is based on total waves across all regions
    // Each wave adds CITIZENS_PER_WAVE citizens
    const totalWaves = 5 * 8; // 5 regions * 8 waves each
    this.goal = totalWaves * SETTINGS.CITIZENS_PER_WAVE;
  }

  public addCitizens(count: number): void {
    const multiplier = this.doubleChainMode ? 2 : 1;
    this.citizens += count * multiplier;
    
    if (this.citizens > this.goal) {
      this.citizens = this.goal;
    }
  }

  public addWaveComplete(): void {
    this.addCitizens(SETTINGS.CITIZENS_PER_WAVE);
  }

  public setRegion(region: number): void {
    this.currentRegion = region;
  }

  public enableDoubleChain(duration: number): void {
    this.doubleChainMode = true;
    this.doubleChainTimer = duration;
  }

  public update(deltaTime: number): void {
    if (this.doubleChainMode) {
      this.doubleChainTimer -= deltaTime;
      if (this.doubleChainTimer <= 0) {
        this.doubleChainMode = false;
      }
    }
  }

  public getBarSize(scaleOverride?: number): { width: number; height: number } {
    const isTouch = (typeof window !== 'undefined') && (('ontouchstart' in window) || window.innerWidth <= 700);
    const baseBarWidth = 300;
    const baseBarHeight = 36;
    const scale = typeof scaleOverride === 'number' ? scaleOverride : (isTouch ? 1.6 : 1.0);
    return { width: Math.round(baseBarWidth * scale), height: Math.round(baseBarHeight * scale) };
  }

  public render(ctx: CanvasRenderingContext2D, x: number, y: number, scaleOverride?: number): void {
    // Responsive sizing: allow an external scale override (used by HUD to center/size)
    const isTouch = (typeof window !== 'undefined') && (('ontouchstart' in window) || window.innerWidth <= 700);
    const baseBarWidth = 300;
    const baseBarHeight = 36;
    const scale = typeof scaleOverride === 'number' ? scaleOverride : (isTouch ? 1.6 : 1.0);
    const barWidth = Math.round(baseBarWidth * scale);
    const barHeight = Math.round(baseBarHeight * scale);
    const progress = this.citizens / this.goal;

    // Background (semi-transparent)
    const prevAlpha = ctx.globalAlpha;
    ctx.globalAlpha = 0.45;
    ctx.fillStyle = '#222222';
    ctx.fillRect(x, y, barWidth, barHeight);

    // Progress fill (slightly more opaque)
    ctx.globalAlpha = 0.9;
    ctx.fillStyle = COLORS.PRIMARY;
    ctx.fillRect(x, y, barWidth * progress, barHeight);

    // Restore alpha for border and text
    ctx.globalAlpha = prevAlpha;

    // Border (subtle)
    ctx.strokeStyle = 'rgba(255,255,255,0.85)';
    ctx.lineWidth = 2;
    ctx.strokeRect(x, y, barWidth, barHeight);

    // Text
    ctx.fillStyle = COLORS.TEXT;
    // compute font sizes scaled
    const labelFontSize = Math.round(14 * scale);
    const valueFontSize = Math.round(18 * scale);
    // Label above the bar (left-aligned)
    ctx.font = `bold ${labelFontSize}px "Courier New"`;
    ctx.textAlign = 'left';
    ctx.fillText('CHAIN', x, y - Math.round(8 * scale));
    // Numbers inside the bar (center)
    ctx.font = `bold ${valueFontSize}px "Courier New"`;
    ctx.textAlign = 'center';
    ctx.fillText(`${this.citizens}/${this.goal}`, x + barWidth / 2, y + barHeight / 2 + Math.round(valueFontSize / 3));

    // Double chain indicator
    if (this.doubleChainMode) {
      ctx.fillStyle = COLORS.ACCENT;
      const dblFont = Math.round(16 * scale);
      ctx.font = `bold ${dblFont}px "Courier New"`;
      ctx.textAlign = 'center';
      ctx.fillText(
        `2X CHAIN!`,
        x + barWidth / 2,
        y - Math.round(22 * scale)
      );
    }
  }

  public getCitizens(): number {
    return this.citizens;
  }

  public getGoal(): number {
    return this.goal;
  }

  public getProgress(): number {
    return this.citizens / this.goal;
  }

  public isComplete(): boolean {
    return this.citizens >= this.goal;
  }

  public isDoubleChainActive(): boolean {
    return this.doubleChainMode;
  }

  public reset(): void {
    this.citizens = 0;
    this.currentRegion = 1;
    this.doubleChainMode = false;
    this.doubleChainTimer = 0;
  }
}
